<template>
    <div
        v-qa="'sku-stone-search'"
        class="stone-search"
    >
        <div
            v-qa="'before-filters-content'"
            class="before-filters-content"
        >
            <ThTitle
                v-if="facetedTitle"
                alignment="CENTER"
                color="var(--blue)"
                tag="H1"
            >
                {{ facetedTitle }}
            </ThTitle>

            <slot
                v-else
                name="before-filters"
            ></slot>
        </div>

        <GridContentLoader v-if="!bounds && fetchingOrStale" />

        <div v-else-if="bounds">
            <div class="quick-filters">
                <div class="filters">
                    <div class="padded">
                        <slot name="filters-quick-padded"></slot>
                    </div>

                    <slot name="filters-quick"></slot>
                </div>
            </div>

            <div class="mobile-stone-tabs">
                <StoneSearchTabs
                    :shape="gemsApiShapeToShapeCode(shape)"
                    class="tabs"
                />
            </div>

            <GridContentLoader v-if="fetchingOrStale && !data" />

            <StoneSearchFiveHundredError
                v-else-if="error"
                :errors
                :route-reset
                @retry="retry"
            />

            <div v-else-if="hasBadFacets">
                <PageErrorFourHundredFour />
            </div>

            <div
                v-else-if="data"
                ref="gridEl"
                v-qa="'stone-grid'"
                v-qa.fetching="fetchingOrStale"
                class="stone-grid-wrapper"
            >
                <div
                    ref="gridTopBarWrapperEl"
                    :class="classGridTopBarWrapper"
                    class="grid-top-bar-wrapper"
                >
                    <div class="grid-top-bar">
                        <div class="left">
                            <FilterButton
                                :active-filter-count
                                @open-filters-modal="openFilterModal"
                            />

                            <div
                                v-qa="'total-results'"
                                class="total-results"
                            >
                                {{ totalResultsCountLabel }}
                            </div>
                        </div>

                        <div class="left grid-toggle">
                            <GridViewToggle v-model:table-view="tableView" />
                        </div>

                        <div class="right">
                            <SortingDropdown
                                :model="sorting"
                                :options="sortingOptions"
                                :route-getter="getSortRoute"
                            />
                        </div>
                    </div>
                </div>

                <div class="grid">
                    <FilterChips
                        v-qa="'filter-chips-page'"
                        :chips="filterChips"
                        :route-reset
                    />

                    <div class="grid-toggle-aux">
                        <GridViewToggle v-model:table-view="tableView" />
                    </div>

                    <div
                        class="sidebar grid-content left"
                        data-qa="left-sidebar"
                    >
                        <slot name="filters"></slot>
                    </div>

                    <div class="grid-content right">
                        <div
                            v-if="page > 98"
                            class="max-page-filters"
                        >
                            <ThTitle
                                alignment="CENTER"
                                color="var(--blue)"
                                size="MEDIUM"
                                tag="H3"
                            >
                                {{
                                    t(
                                        'diamond_search.still_looking_for_the_perfect_one'
                                    )
                                }}
                            </ThTitle>

                            <ThTitle
                                alignment="CENTER"
                                size="SMALL"
                                tag="H4"
                            >
                                {{
                                    t(
                                        'diamond_search.please_adjust_your_filters'
                                    )
                                }}
                            </ThTitle>

                            <slot name="last-page-filters"></slot>
                        </div>

                        <div
                            v-else-if="stonesCollection?.length"
                            class="wrapper"
                        >
                            <GridTableHead v-if="tableView" />

                            <div
                                :class="classCards"
                                class="cards"
                            >
                                <div
                                    v-for="stone in stonesCollection"
                                    :key="stone.identifier"
                                    class="card"
                                >
                                    <slot
                                        :stone
                                        :table-view
                                        name="preview"
                                    ></slot>
                                </div>

                                <NoResultCta
                                    v-if="stonesCollection.length < 6"
                                    :category
                                    :small="!tableView"
                                />
                            </div>

                            <Pagination
                                v-if="totalCount"
                                :count="totalCount"
                                :current="page"
                                :get-route-page="paginationTo"
                                :limit="productsPerPage"
                                :max-page="99"
                                :range="2"
                                :scroll-into-view-offset="{
                                    left: 0,
                                    top: 60,
                                }"
                                scroll-into-view-selector=".shape-filter"
                            />
                        </div>

                        <GridContentLoader v-else-if="fetchingOrStale" />

                        <div v-else>
                            <div class="no-results-found">
                                <ThSpacer size="LARGE" />

                                <slot name="no-results-found"></slot>

                                <NoResultCta :category />

                                <ThSpacer size="LARGE" />
                            </div>
                        </div>
                    </div>
                </div>

                <FacetedContent
                    v-if="facetedContent"
                    :content="facetedContent"
                />

                <slot
                    v-else
                    name="after-grid"
                ></slot>
            </div>

            <div v-else>
                <StoneSearchFiveHundredError
                    :errors
                    :route-reset
                    @retry="retry"
                />
            </div>
        </div>

        <div v-else>
            <StoneSearchFiveHundredError
                :errors
                :route-reset
                @retry="retry"
            />
        </div>

        <StoneModal
            :stone-id
            @close="$emit('close-stone-modal')"
            @select="selectStone"
        />

        <FiltersModal
            v-if="isFilterModalRendered"
            :active-filter-count
            :data-qa="'filters-modal'"
            :fetching="fetchingOrStale"
            :is-open="isFilterModalOpen"
            :result-count="totalCount || 0"
            :route-reset
            @close="closeFilterModal"
        >
            <template #chips>
                <slot name="filters-chips"></slot>
            </template>

            <template #filters>
                <slot name="filters"></slot>
            </template>
        </FiltersModal>
    </div>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent, hydrateOnVisible, toRef } from 'vue'
import { useI18n } from 'vue-i18n'

import {
    GemsApi_SortingDirection,
    GemsApi_SortingField,
    type GemsApi_StoneCategory,
    type GemsApi_StoneFilters,
    type GemsApi_StoneShape,
    type GemsApi_StoneSorting,
    type GemsBoundsFragment,
    type GemsFindStonesQuery,
    type StoneFragment,
} from '@/graphql/api'
import type { AnyVariables, UseQueryResponse } from '@urql/vue'
import { useRingBuilder } from '@/composables/useRingBuilder'
import { useRoutes } from '@/router/routes'
import { type DecodedStoneFiltersAndIdentifier } from '@/domain/stone'
import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import type { ErrorCollectionWithTraces } from '@/helpers/errors'
import { useRouterNavigation } from '@/plugins/released-urls/useRouterNavigation'
import { useStickyGridFiltersBar } from '@/components/composables/useStickyGridFiltersBar'
import { useNumberFormatter } from '@/composables/useNumberFormatter'
import { useGridViewToggle } from '../useGridViewToggle'

import StoneModal from '@/components/stone/search/sku/StoneModal.vue'
import FiltersModal from '@/components/ui/filter/FiltersModal.vue'
import ThTitle from '@/components/ui/title/ThTitle.vue'
import SortingDropdown, {
    type DropdownOption,
} from '@/components/ui/SortingDropdown.vue'
import FilterButton from '@/components/product/FilterButton.vue'
import ThSpacer from '@/components/ui/ThSpacer.vue'
import FilterChips, {
    type FilterChip,
} from '@/components/ui/filter/FilterChips.vue'
import GridViewToggle from '@/components/stone/search/GridViewToggle.vue'
import { gemsApiShapeToShapeCode } from '@/domain/stone/search/sku/shape.ts'
import StoneSearchTabs from '@/components/stone/search/StoneSearchTabs.vue'
import { useFacetedContent } from '@/domain/product/useFacetedContent.ts'
import { useMeta } from '@/composables/app/useMeta.ts'
import { useFilterModal } from '@/composables/filter-modal.ts'

const Pagination = defineAsyncComponent({
    loader: () => import('@/components/ui/Pagination.vue'),
    hydrate: hydrateOnVisible(),
})

const FacetedContent = defineAsyncComponent({
    loader: () => import('@/components/product/FacetedContent.vue'),
    hydrate: hydrateOnVisible(),
})

const GridContentLoader = defineAsyncComponent({
    loader: () => import('@/components/ui/GridContentLoader.vue'),
    hydrate: hydrateOnVisible(),
})

const GridTableHead = defineAsyncComponent({
    loader: () => import('../GridTableHead.vue'),
    hydrate: hydrateOnVisible(),
})

const NoResultCta = defineAsyncComponent({
    loader: () => import('../NoResultCta.vue'),
    hydrate: hydrateOnVisible(),
})

const StoneSearchFiveHundredError = defineAsyncComponent({
    loader: () => import('../StoneSearchFiveHundredError.vue'),
    hydrate: hydrateOnVisible(),
})

const PageErrorFourHundredFour = defineAsyncComponent({
    loader: () => import('@/components/PageErrorFourHundredFour.vue'),
    hydrate: hydrateOnVisible(),
})

const props = defineProps<{
    category: GemsApi_StoneCategory
    bounds?: GemsBoundsFragment | null
    data: GemsFindStonesQuery | null | undefined
    filters?: GemsApi_StoneFilters
    fetching: boolean
    stale: boolean
    hasBadFacets: boolean
    response: UseQueryResponse<GemsFindStonesQuery, AnyVariables>
    shape?: GemsApi_StoneShape
    sorting: GemsApi_StoneSorting
    page: number
    productsPerPage: number
    paginationTo: (page: number) => ButtonRouteRelative
    hashParams: DecodedStoneFiltersAndIdentifier | undefined
    errors: ErrorCollectionWithTraces
    routeReset: ButtonRouteRelative
    filterChips: FilterChip[]
    getSortRoute: (sort: GemsApi_StoneSorting) => ButtonRouteRelative
    activeFilterCount: number
}>()

defineEmits<{
    'close-stone-modal': []
}>()

defineSlots<{
    preview(props: {
        stone: StoneFragment
        tableView: boolean | undefined
    }): any
    'before-filters': () => any
    'filters': () => any
    'filters-chips': () => any
    'after-grid': () => any
    'last-page-filters': () => any
    'filters-quick': () => any
    'filters-quick-padded': () => any
    'no-results-found': () => any
}>()

const { t } = useI18n()

const sortingOptions: DropdownOption<GemsApi_StoneSorting>[] = [
    {
        value: {
            direction: GemsApi_SortingDirection.Asc,
            field: GemsApi_SortingField.Price,
        },
        label: 'Price (low to high)',
    },
    {
        value: {
            direction: GemsApi_SortingDirection.Desc,
            field: GemsApi_SortingField.Price,
        },
        label: 'Price (high to low)',
    },
    {
        value: {
            direction: GemsApi_SortingDirection.Asc,
            field: GemsApi_SortingField.Weight,
        },
        label: 'Weight (low to high)',
    },
    {
        value: {
            direction: GemsApi_SortingDirection.Desc,
            field: GemsApi_SortingField.Weight,
        },
        label: 'Weight (high to low)',
    },
]

const executeQuery = toRef(() => props.response.executeQuery)
const data = toRef(() => props.data)

const stonesCollection = toRef(() => data.value?.GemsApi_findStones?.collection)
const error = toRef(() => props.response.error.value)

const totalCount = toRef(() => data.value?.GemsApi_findStones?.pagination.total)
const numberFormatter = useNumberFormatter(0)

const totalResultsCountLabel = computed(() => {
    const count = totalCount.value
    if (count === undefined) {
        return ''
    }

    return t('catalogue.grid.results', count, {
        named: { count: numberFormatter.format(count) },
    })
})

const {
    isFilterModalOpen,
    isFilterModalRendered,
    openFilterModal,
    closeFilterModal,
} = useFilterModal()

const retry = () => {
    executeQuery.value({
        requestPolicy: 'network-only',
    })
}

const fetchingOrStale = computed(() => props.fetching || props.stale)

const stoneId = computed(() => props.hashParams?.stoneIdentifier)

const routes = useRoutes()
const { routerPush } = useRouterNavigation()

const {
    facetedTitle,
    facetedContent,
    facetedMetaDescription,
    facetedMetaTitle,
} = useFacetedContent(
    computed(() => ({
        type: 'diamond-search' as const,
        filters: props.bounds,
        filterValues: props.filters,
    })),
    toRef(() => data.value?.pageProductListingFacetedContent),
    computed(() => (!props.shape ? 0 : 1)),
    toRef(() => data.value?.pageProductListing?.meta)
)

useMeta(
    {
        title: () => facetedMetaTitle.value,
        description: () => facetedMetaDescription.value,
    },
    data,
    toRef(() => props.fetching),
    toRef(() => props.stale)
)

const { ring } = useRingBuilder()
const selectStone = (v: StoneFragment) => {
    if (ring.value) {
        routerPush(routes.ringBuilder.ring(ring.value, v))
    } else {
        routerPush(
            routes.engagementRings({
                shape: v.shapeWithInfo.info?.systemCode,
            })
        )
    }
}

const { tableView, classCards } = useGridViewToggle(fetchingOrStale)

const {
    gridTopBarWrapperEl,
    gridEl,
    classGridTopBarWrapper,
    gridTopBarHeight,
} = useStickyGridFiltersBar()
</script>

<style lang="scss" scoped>
@include stone-search;

.grid-top-bar-wrapper {
    height: v-bind(gridTopBarHeight);
}

.grid-top-bar,
.grid-toggle-aux {
    width: 1440px;
    max-width: 100%;

    font-family: $effra-font-stack;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.875rem;
}

.grid-toggle-aux {
    margin-bottom: 10px;

    @include min-width(768) {
        display: none;
    }
}

.grid-top-bar {
    margin: 0 auto;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    @include min-width(360) {
        padding: 0 20px;
    }

    @include min-width(1280) {
        padding: 0 40px;
    }

    .left {
        display: flex;
        margin-right: 12px;
    }

    .right {
        display: flex;
        margin-left: auto;
    }

    .grid-toggle {
        display: none;

        @include min-width(768) {
            display: block;
        }
    }

    .total-results {
        display: none;
        color: $grey-400;

        @include min-width(640) {
            display: block;
        }
    }
}

.grid-content {
    &.right {
        width: 100%;
    }
}
</style>
